import '~/shared/styles/nprogress.scss';

import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  from,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';
import { relayStylePagination } from '@apollo/client/utilities';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import { createConsumer } from '@rails/actioncable';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { captureException } from '@sentry/nextjs';
import { SentryLink } from 'apollo-link-sentry';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { ConfirmProvider } from 'material-ui-confirm';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { SnackbarProvider, useSnackbar } from 'notistack';
import NProgress from 'nprogress';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import PageChangeProgressBar from '~/shared/components/PageChangeProgressBar';
import { MAINTENANCE_ROUTE } from '~/shared/constants/routes';
import { AppDataProvider } from '~/shared/contexts/AppDataContext';
import {
  AuthContextProvider,
  getAccessToken,
  useAuth,
} from '~/shared/contexts/AuthContext';
import GrowthBookContextProvider from '~/shared/contexts/GrowthBookContext';
import { GymTimezoneProvider } from '~/shared/contexts/GymTimezoneContext';
import { SettingsContextProvider } from '~/shared/contexts/SettingsContext';
import useTheme from '~/shared/styles/useTheme';
import { parseErrorMessages } from '~/shared/utils/errorMessages';
import { getApiUrl } from '~/shared/utils/urlUtil';

const ThemedBasePage = ({ children }) => {
  const theme = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <ConfirmProvider>
        <SnackbarProvider
          maxSnack={1}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline />
          {children}
        </SnackbarProvider>
      </ConfirmProvider>
    </ThemeProvider>
  );
};

const ApolloContextProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { gymId, setOAuthData } = useAuth();
  const graphqlUri = useMemo(() => `${getApiUrl()}/graphql`, []);

  const gymIdRef = useRef(gymId);
  gymIdRef.current = gymId;
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const setOAuthDataRef = useRef(setOAuthData);
  setOAuthDataRef.current = setOAuthData;

  const hasSubscriptionOperation = useCallback(({ query: { definitions } }) => {
    return definitions.some(
      ({ kind, operation }) =>
        kind === 'OperationDefinition' && operation === 'subscription'
    );
  }, []);

  const httpLink = useMemo(
    () =>
      new BatchHttpLink({
        uri: graphqlUri,
        batchMax: 10,
        batchInterval: 20,
      }),
    [graphqlUri]
  );

  const splitLink = useMemo(() => {
    const cable = createConsumer(() => {
      const accessToken = getAccessToken();
      return `${getApiUrl()}/cable?access_token=${accessToken}`;
    });

    return ApolloLink.split(
      hasSubscriptionOperation,
      new ActionCableLink({ cable }),
      httpLink
    );
  }, [hasSubscriptionOperation, httpLink]);

  const authLink = useMemo(() => {
    return setContext((_, { headers }) => {
      const accessToken = getAccessToken();
      return {
        headers: {
          ...headers,
          authorization: accessToken ? `Bearer ${accessToken}` : '',
          'X-Gym-Id': gymIdRef.current,
          ...(CONFIG.cfAccessClientId && CONFIG.cfAccessClientSecret && {
            'CF-Access-Client-Id': CONFIG.cfAccessClientId,
            'CF-Access-Client-Secret': CONFIG.cfAccessClientSecret,
          }),
        },
      };
    });
  }, []);

  const errorLink = useMemo(
    () =>
      onError(errorResponse => {
        const { graphQLErrors, networkError } = errorResponse;

        captureException(new ApolloError({ graphQLErrors, networkError }));

        if (graphQLErrors) {
          graphQLErrors.forEach(({ message: _0, locations: _1, path: _2 }) => {
            // console.error(
            //   `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            // )
          });

          const errorMessages = parseErrorMessages(graphQLErrors);

          if (errorMessages) {
            enqueueSnackbarRef.current(errorMessages, {
              variant: 'error',
            });
          }
        }

        if (networkError) {
          // console.error(`[Network error]: ${networkError}`);

          const errorMessages = parseErrorMessages(
            (networkError as ServerError).result
          );

          if (errorMessages) {
            enqueueSnackbarRef.current(errorMessages, {
              variant: 'error',
            });
          }

          if ((networkError as ServerError).statusCode === 401) {
            setOAuthDataRef.current(null);
          }
        }
      }),
    []
  );

  const apolloClient = useMemo(
    () =>
      new ApolloClient({
        link: from([
          new SentryLink({
            uri: graphqlUri,
            attachBreadcrumbs: {
              includeQuery: true,
              includeFetchResult: true,
              includeError: true,
            },
          }),
          errorLink,
          authLink,
          splitLink,
        ]),
        cache: new InMemoryCache({
          typePolicies: {
            Query: {
              fields: {
                memberTransactions: relayStylePagination(),
              },
            },
            Member: {
              fields: {
                photoUrl: {
                  merge(existing, incoming) {
                    return incoming ?? existing ?? null;
                  },
                },
              },
            },
          },
        }),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'cache-and-network',
          },
        },
      }),
    [authLink, errorLink, graphqlUri, splitLink]
  );

  const prevGymIdRef = useRef(gymId);
  useEffect(() => {
    if (prevGymIdRef.current && prevGymIdRef.current !== gymId) {
      apolloClient.resetStore();
    }
    prevGymIdRef.current = gymId;
  }, [gymId, apolloClient]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const MyApp: FC<AppProps> = ({ Component, pageProps }: AppProps) => {
  const { replace, pathname } = useRouter();

  const queryClient = new QueryClient();

  React.useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement?.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    NProgress.configure({ showSpinner: false });
  }, []);

  useEffect(() => {
    if (CONFIG.maintenanceMode && pathname !== MAINTENANCE_ROUTE) {
      replace(MAINTENANCE_ROUTE);
    }
  }, [pathname, replace]);

  return (
    <GoogleOAuthProvider clientId={CONFIG.googleClientId}>
      <React.Fragment>
        <Head>
          <title>{'Fitquarters'}</title>
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width"
          />
        </Head>
        <PageChangeProgressBar>
          <AuthContextProvider>
            <GrowthBookContextProvider>
              <SettingsContextProvider>
                <ThemedBasePage>
                  <ApolloContextProvider>
                    <AppDataProvider>
                      <GymTimezoneProvider>
                        <QueryClientProvider client={queryClient}>
                          <Component {...pageProps} />
                        </QueryClientProvider>
                      </GymTimezoneProvider>
                    </AppDataProvider>
                  </ApolloContextProvider>
                </ThemedBasePage>
              </SettingsContextProvider>
            </GrowthBookContextProvider>
          </AuthContextProvider>
        </PageChangeProgressBar>
      </React.Fragment>
    </GoogleOAuthProvider>
  );
};

export default MyApp;
